import { Result, Ok, Err, validate as v, exhaust } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { ContentJson } from "GenAlphaModel";
import type { InteractionHost } from "../interaction_host";
import { ContentKind } from "DrawCore/src/content/kind";
import { GraphicsContent } from "./graphics/graphics";
import { Schematic } from "./schematic/schematic";
import { Symbol } from "./symbol/symbol";
import { LayoutContent } from "./layout/content";

// The `Content` union type.
export type Content = GraphicsContent | Schematic | Symbol | LayoutContent;
export const Content = {
  Kind: ContentKind,

  // The default content. Generally invoked at editor start. Maybe we wanna avoid somehow? Dunno how.
  default: (editor: InteractionHost): Content => new GraphicsContent(editor),

  // Create a new & empty piece of content
  createNew: (
    editor: InteractionHost,
    contentKind?: string
  ): Result<Content> => {
    switch (contentKind) {
      case ContentKind.Schematic:
        return Ok(Schematic.createNew(editor));
      case ContentKind.Symbol:
        return Ok(Symbol.createNew(editor));
      // case ContentKind.Layout:
      //   return Ok(LayoutContent.createNew(editor));
      default:
        return Err({ why: "invalid-content-kind", contentKind });
    }
  },

  // Create from a schema object
  fromSchema: async (
    editor: InteractionHost,
    data: schema.Content
  ): Promise<Result<Content>> => {
    const { contentKind } = data;
    switch (contentKind) {
      case "schematic":
        return Schematic.fromSchema(editor, data);
      case "symbol":
        return Symbol.fromSchema(editor, data);
      case "graphics":
        return GraphicsContent.fromSchema(editor, data);
      case "layout":
        return LayoutContent.fromSchema(editor, data);
      case "circuit":
        return Err({ why: "unsupported-content-kind" as const });
      default:
        exhaust(contentKind);
    }
  },

  // Create from an object that has a `contentKind` field
  fromKind: async (
    editor: InteractionHost,
    data: HasContentKind
  ): Promise<Result<Content>> => {
    return ContentJson.validator
      .validate(data)
      .andThenAsync((x) => Content.fromSchema(editor, x));
  },

  // Create from unknown (generally JSON-parsed) data
  fromUnknown: async (
    editor: InteractionHost,
    data: unknown
  ): Promise<Result<Content>> => {
    // The `unknown` type is an extra hassle.
    // We use this `HasContentKind` to verify (a) that it's an object, and (b) has a string-valued `ContentKind`.
    // Then hand off for further type-specific validation from there.
    return HasContentKind.validator.validate(data).andThenAsync((x) => {
      return Content.fromKind(editor, {
        contentKind: x.contentKind,
        // NOTE: we know `data` is an object, because it passed the `HasContentKind` validator
        // The validator (sadly) does not propagate all the other fields onto `x`.
        ...(data as object),
      });
    });
  },
};

interface HasContentKind {
  contentKind: string;
}
const HasContentKind = {
  validator: new v.Validator(v.createValidate<HasContentKind>()),
};

// FIXME! not really a great name any more
export type NonGraphicsContent = Schematic | Symbol | GraphicsContent;
